import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { Company } from '@/types/api'

import {
  createCompany,
  exportCompany,
  listCompanies,
  updateCompany,
  type CreateCompanyInput,
  type UpdateCompanyInput,
} from '@/lib/api/endpoints'
import { createOptimisticListMutation } from '@/lib/queries/_helpers'
import { queryKeys } from '@/lib/queries/keys'
import { useCompanyStore } from '@/stores/company'

export function useCompanies() {
  return useQuery({
    queryKey: queryKeys.companies.list(),
    queryFn: listCompanies,
  })
}

const TEMP_PREFIX = 'tmp_cmp_'

export function useCreateCompany() {
  const qc = useQueryClient()

  return useMutation(
    createOptimisticListMutation<Company[], CreateCompanyInput, Company>({
      queryClient: qc,
      queryKey: queryKeys.companies.list(),
      mutationFn: (input) => createCompany(input),
      applyOptimistic: (current, input) => {
        const optimistic: Company = {
          id: `${TEMP_PREFIX}${Date.now()}`,
          name: input.name,
          mission: input.mission,
          // ownerUserId real virá do backend (VEC-140 — auth). Por enquanto
          // o handler MSW preenche com o usuário hardcoded `usr_marcelo`.
          ownerUserId: 'usr_marcelo',
          slug:
            input.name
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/^-|-$/g, '') || 'new-company',
          members: [
            {
              userId: 'usr_marcelo',
              role: 'admin',
              joinedAt: new Date().toISOString(),
            },
          ],
          createdAt: new Date().toISOString(),
        }
        return [...(current ?? []), optimistic]
      },
    }),
  )
}

export function useUpdateCompany() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ companyId, patch }: { companyId: string; patch: UpdateCompanyInput }) =>
      updateCompany(companyId, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.companies.all })
    },
  })
}

export function useExportCompany() {
  return useMutation({
    mutationFn: (companyId: string) => exportCompany(companyId),
  })
}

/**
 * Garante que sempre exista uma `activeCompanyId` setada quando a lista de
 * companies estiver disponível. Se o store estiver vazio, seleciona a primeira.
 * Usado em hooks consumidores como `useActiveCompanyId`.
 */
export function useEnsureActiveCompany() {
  const { data: companies } = useCompanies()
  const activeCompanyId = useCompanyStore((s) => s.activeCompanyId)
  const setActiveCompany = useCompanyStore((s) => s.setActiveCompany)

  useEffect(() => {
    if (!companies || companies.length === 0) return
    const stillExists = companies.some((c) => c.id === activeCompanyId)
    if (!stillExists) {
      const first = companies[0]
      if (first) setActiveCompany(first.id)
    }
  }, [companies, activeCompanyId, setActiveCompany])
}

/**
 * Retorna o id da company ativa, ou null enquanto a lista de companies não
 * carregou. Isso evita abrir WebSocket com um id stale do localStorage
 * (ex.: "cmp_acme" de uma sessão mock anterior) antes da API confirmar o ID real.
 */
export function useActiveCompanyId(): string | null {
  const { data: companies, isLoading } = useCompanies()
  useEnsureActiveCompany()
  const activeCompanyId = useCompanyStore((s) => s.activeCompanyId)

  // Enquanto a query ainda não resolveu, não confiar no valor do localStorage.
  if (isLoading) return null

  // IDs temporários (prefixo tmp_cmp_) nunca são válidos para usar em requests.
  if (activeCompanyId?.startsWith(TEMP_PREFIX)) return null

  // Valor validado: só retorna se existe na lista retornada pela API.
  const valid = companies?.some((c) => c.id === activeCompanyId)
  return valid ? activeCompanyId : null
}
